import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Loader2, Save } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Getting Started", "Styling", "Prompts", "Troubleshooting", "Cleanup", "Usage", "Support", "Walkthrough", "Other"];
const blank = { title: "", description: "", category: "Getting Started", content_type: "embedded", content: "", display_order: 10, is_active: true };

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/admin/resources");
    setItems(r.data.resources || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm(blank);
    setEditing("new");
  };

  const startEdit = (it) => {
    setForm({
      title: it.title || "",
      description: it.description || "",
      category: it.category || "Getting Started",
      content_type: it.content_type || "embedded",
      content: it.content || "",
      display_order: it.display_order || 10,
      is_active: it.is_active !== false,
    });
    setEditing(it.id);
  };

  const close = () => {
    setEditing(null);
    setForm(blank);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/resources", form);
        toast.success("Resource created");
      } else {
        await api.put(`/admin/resources/${editing}`, form);
        toast.success("Resource updated");
      }
      close();
      load();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (it) => {
    await api.put(`/admin/resources/${it.id}`, {
      title: it.title,
      description: it.description || "",
      category: it.category || "",
      content_type: it.content_type || "embedded",
      content: it.content || "",
      display_order: it.display_order || 0,
      is_active: !it.is_active,
    });
    load();
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete resource "${it.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/resources/${it.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-resources">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow">Resources</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#1A1F3D] mt-2">Member resource library</h1>
        </div>
        <button onClick={startNew} className="btn-primary" data-testid="add-resource">
          <Plus className="w-4 h-4" /> Add new resource
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#D4A853] animate-spin" /></div>
      ) : (
        <div className="mt-6 bsp-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF8] text-[#52525b] text-left text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-3 font-semibold">Title</th>
                  <th className="p-3 font-semibold">Category</th>
                  <th className="p-3 font-semibold">Type</th>
                  <th className="p-3 font-semibold text-right">Order</th>
                  <th className="p-3 font-semibold">Active</th>
                  <th className="p-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-[#e5e7eb] hover:bg-[#FAFAF8]" data-testid={`resource-row-${it.id}`}>
                    <td className="p-3 font-medium text-[#1A1F3D]">{it.title}</td>
                    <td className="p-3 text-[#52525b]">{it.category}</td>
                    <td className="p-3 text-[#52525b]">{it.content_type}</td>
                    <td className="p-3 text-right">{it.display_order}</td>
                    <td className="p-3">
                      <button onClick={() => toggleActive(it)} className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${it.is_active ? "bg-[#D4A853]/20 text-[#1A1F3D]" : "bg-[#F5E6E0] text-[#52525b]"}`}>
                        {it.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <button onClick={() => startEdit(it)} className="w-9 h-9 rounded-md hover:bg-[#F5E6E0] text-[#1A1F3D] inline-flex items-center justify-center" data-testid={`edit-${it.id}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(it)} className="w-9 h-9 rounded-md hover:bg-red-50 text-red-600 inline-flex items-center justify-center" data-testid={`del-${it.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-[#52525b]">No resources yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <form onSubmit={save} className="bg-white rounded-2xl p-7 w-full max-w-2xl relative" onClick={(e) => e.stopPropagation()} data-testid="resource-form">
            <button type="button" onClick={close} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#FAFAF8] flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
            <span className="eyebrow">{editing === "new" ? "New resource" : "Edit resource"}</span>
            <h2 className="font-display text-2xl text-[#1A1F3D] mt-1">{editing === "new" ? "Add a new member resource" : "Update resource"}</h2>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#1A1F3D]">Title</label>
                <input className="bsp-input mt-1" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="r-title" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A1F3D]">Description</label>
                <input className="bsp-input mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="r-desc" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Category</label>
                  <select className="bsp-input mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="r-cat">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Content type</label>
                  <select className="bsp-input mt-1" value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} data-testid="r-type">
                    <option value="embedded">Embedded text</option>
                    <option value="external_link">External link / URL</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A1F3D]">
                  {form.content_type === "external_link" ? "URL" : "Content"}
                </label>
                {form.content_type === "external_link" ? (
                  <input className="bsp-input mt-1" placeholder="https://…" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="r-content" />
                ) : (
                  <textarea className="bsp-input mt-1" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="r-content" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Display order</label>
                  <input type="number" className="bsp-input mt-1" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "0", 10) })} data-testid="r-order" />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#D4A853]" data-testid="r-active" />
                    Active
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-7 flex justify-end gap-2">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="r-save">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save resource"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
